import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n'; // i18n 초기화
import CONFIG from './config';
import LoginPage from './login/LoginPage';
import OAuthCallback from './login/OAuthCallback';
import RenderPage from './RenderPage';
import { ApiDocumentPage } from './api-document';
import { EditorPage } from './editor';
import { auth, db } from './firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import {
  fetchPptList,
  fetchPptSlides,
  fetchSlideThumbnails,
  fetchTotalText,
  deletePptDocument
} from './services/pptService';
// import { subscribeToSessionChanges } from './services/sessionService'; // 다중 로그인 허용
import {
  fetchAllSlideTypoResults
} from './services/typoResultService';
import {
  saveColorDraft,
  saveFontDraft,
  saveTypoDraft,
  deleteColorDraft,
  deleteFontDraft,
  deleteTypoDraft
} from './services/draftService';

// Custom hooks
import useTypoAnalysis from './hooks/useTypoAnalysis';
import useUpload from './hooks/useUpload';
// useSidebarResize 제거 - 사이드바 고정 너비로 변경
import { useColorAnalysis } from './hooks/useColorAnalysis';
import { useBrandKit } from './hooks/useBrandKit';
import { useFontAnalysis } from './hooks/useFontAnalysis';
import { useTranslateAnalysis } from './hooks/useTranslateAnalysis';
import { useAlertModal } from './hooks/useAlertModal';

// Components
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import PPTCheckTabs from './components/PPTCheckTabs';
import ProjectView from './components/ProjectView';
import BrandKitViewer from './components/brandkit/BrandKitViewer';
import SettingsView from './components/SettingsView';
import CreditHistoryPage from './components/CreditHistoryPage';
import ResultModal from './components/colorFont/ResultModal';
import AlertModal from './components/AlertModal';

// Styles
import { createAppStyles } from './styles/appStyles';

function PPTAnalysisPage() {
  const CLOUD_FUNCTION_URL = CONFIG.CLOUD_FUNCTION_URL;

  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  // Basic states
  const [slides, setSlides] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [pptItems, setPptItems] = useState([]); // 객체 배열로 변경
  const [selectedPpt, setSelectedPpt] = useState(''); // docId

  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [credit, setCredit] = useState(0); // 기본값 0원 (Firestore에서 로드)
  // 사용자 설정 (settings 필드)
  const [maxUploadSizeMb, setMaxUploadSizeMb] = useState(100);
  const [maxPptCount, setMaxPptCount] = useState(10);
  const [language, setLanguage] = useState('ko');
  const [totalText, setTotalText] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);  // PDF URL for typo result modal
  const [pdfDoc, setPdfDoc] = useState(null);  // Cached PDF document object
  const [typoSlideSize, setTypoSlideSize] = useState(null);  // 슬라이드 크기 (EMU 단위) - 오타 위치 매칭용
  const [predictionData, setPredictionData] = useState(null);  // 크레딧 예측 데이터
  const [pptActiveTab, setPptActiveTab] = useState('typo');
  const [isSlideLoading, setIsSlideLoading] = useState(false);
  // useEnhancedThumbnails 항상 true로 고정
  const useEnhancedThumbnails = true;
  const [brandKitUpdateTrigger, setBrandKitUpdateTrigger] = useState(0);

  // 화면 전환 상태 (project: 프로젝트 화면, analysis: 분석도구 화면, brandkit: 브랜드키트 화면)
  // 초기 접속 시 파일관리 화면으로 시작
  const [activeView, setActiveView] = useState(() => {
    return location.state?.openBrandKit ? 'brandkit' : 'project';
  });

  // 세션 강제 로그아웃 모달 상태 (다중 로그인 허용으로 비활성화)
  // const [showForceLogoutModal, setShowForceLogoutModal] = useState(false);
  // const [forceLogoutDeviceInfo, setForceLogoutDeviceInfo] = useState('');

  // ResultModal 상태
  const [showColorResultModal, setShowColorResultModal] = useState(false);
  const [showFontResultModal, setShowFontResultModal] = useState(false);
  const [modalSlideIndex, setModalSlideIndex] = useState(0);
  const [violationColorChanges, setViolationColorChanges] = useState({});
  const [violationFontChanges, setViolationFontChanges] = useState({});

  // 모달 열 때의 초기 상태 저장 (닫을 때 비교용)
  const colorInitialStateRef = useRef({});
  const fontInitialStateRef = useRef({});

  // 칼라/폰트 모달 버튼 로딩 상태
  const [isModifyingColor, setIsModifyingColor] = useState(false);
  const [isModifyingFont, setIsModifyingFont] = useState(false);

  // AlertModal 훅 (통합 관리)
  const {
    showAlert,
    alertMessage,
    alertType,
    closeAlert,
    showConfirm,
    confirmMessage,
    handleConfirm,
    handleCancel,
    showAlertModal,
    showConfirmModal
  } = useAlertModal();

  const {
    // Upload states
    uploading,
    uploadProgress,
    uploadDone,
    uploadStatus,
    error,

    // Upload functions
    handlePptUpload: handleUpload,
    setError
  } = useUpload(useEnhancedThumbnails, maxUploadSizeMb);

  // 사이드바 상태 - 고정 너비, 항상 열림 (모바일 제외)
  const sidebarWidth = 280;
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // 브랜드키트 훅 (색상 + 폰트 통합)
  const { colors, fonts, setFonts, presets, activePresetId, loadAllPresets } = useBrandKit(userId, brandKitUpdateTrigger);

  // 폰트 변경 핸들러 (폰트 검사 화면에서 사용)
  const handleFontChange = useCallback(async (index, newFont) => {
    try {
      // 로컬 상태 먼저 업데이트
      const newFonts = [...fonts];
      newFonts[index] = newFont;
      setFonts(newFonts);

      // Firestore에 저장
      if (userId && presets && activePresetId) {
        const currentPreset = presets[activePresetId];
        const presetData = {
          name: currentPreset?.name || `프리셋 ${activePresetId.slice(-1)}`,
          colors: colors,
          fonts: newFonts
        };

        const { saveUserBrandKitPreset } = await import('./services/brandKitService');
        await saveUserBrandKitPreset(userId, activePresetId, presetData);

        // 프리셋 다시 로드
        await loadAllPresets();
        showAlertModal(t('messages.fontChanged'), 'success');
      }
    } catch (error) {
      console.error('폰트 변경 오류:', error);
      showAlertModal(t('messages.fontChangeError'), 'error');
    }
  }, [fonts, setFonts, userId, presets, activePresetId, colors, loadAllPresets, showAlertModal, t]);

  // TypoResultModal 상태
  const [showTypoResultModal, setShowTypoResultModal] = useState(false);

  // 분석 완료 시 결과 모달 열기 콜백
  const handleColorAnalysisComplete = useCallback(() => {
    setModalSlideIndex(0);
    setShowColorResultModal(true);
  }, []);

  const handleFontAnalysisComplete = useCallback(() => {
    setModalSlideIndex(0);
    setShowFontResultModal(true);
  }, []);

  const handleTypoAnalysisComplete = useCallback(() => {
    setShowTypoResultModal(true);
  }, []);


  // Draft 저장 핸들러
  const handleSaveColorDraft = useCallback(async (changes) => {
    if (!selectedPpt || !userId) return;

    // 변경사항이 없으면 기존 draft 삭제
    const hasChanges = changes && Object.keys(changes).length > 0;
    if (!hasChanges) {
      await deleteColorDraft(selectedPpt);
      showAlertModal(t('draft.deleteSuccess'), 'success');
      colorInitialStateRef.current = {};
      return;
    }

    const result = await saveColorDraft(selectedPpt, userId, changes);
    if (result) {
      showAlertModal(t('draft.saveSuccess'), 'success');
      // 저장 성공 시 초기 상태 업데이트
      colorInitialStateRef.current = JSON.parse(JSON.stringify(changes));
    } else {
      showAlertModal(t('draft.saveError'), 'error');
    }
  }, [selectedPpt, userId, showAlertModal, t]);

  const handleSaveFontDraft = useCallback(async (changes) => {
    if (!selectedPpt || !userId) return;

    // 변경사항이 없으면 기존 draft 삭제
    const hasChanges = changes && Object.keys(changes).length > 0;
    if (!hasChanges) {
      await deleteFontDraft(selectedPpt);
      showAlertModal(t('draft.deleteSuccess'), 'success');
      fontInitialStateRef.current = {};
      return;
    }

    const result = await saveFontDraft(selectedPpt, userId, changes);
    if (result) {
      showAlertModal(t('draft.saveSuccess'), 'success');
      // 저장 성공 시 초기 상태 업데이트
      fontInitialStateRef.current = JSON.parse(JSON.stringify(changes));
    } else {
      showAlertModal(t('draft.saveError'), 'error');
    }
  }, [selectedPpt, userId, showAlertModal, t]);

  // 오타 분석 훅 (새로운 시그니처)
  const {
    // Typo check states
    selectedSlideTypos,
    applyingTypoCorrections,
    editedSuggestions,
    slideTypoResults,
    slideTypoCheckStatus,
    isAnalyzingAll: isTypoAnalyzingAll,
    isAnySingleSlideChecking: isTypoAnySingleSlideChecking,
    checkAllProgress,
    hasTypoDraft,
    loadedTypoDraft,

    // Typo check functions
    checkAllSlidesTypos,
    checkSlideTypos,
    toggleSlideTypoSelection,
    selectAllSlideTypos,
    deselectAllSlideTypos,
    updateEditedSuggestion,
    getEditedSuggestion,
    applySlideTypoCorrections,
    resetTypoStates,
    initializeSlideTypoStatus,
    applyTypoDraft,
    clearTypoDraft,
    setSlideTypoResults,
    setSlideTypoCheckStatus
  } = useTypoAnalysis(
    selectedPpt,
    userId,
    slides,
    credit,
    predictionData,
    showAlertModal,
    showConfirmModal,
    handleTypoAnalysisComplete,
    t
  );

  // 오타 draft 저장 핸들러 (훅 호출 이후 정의)
  const handleSaveTypoDraft = useCallback(async () => {
    if (!selectedPpt || !userId) return;

    // selected_typos 필터링: 빈 배열 제외
    const filteredSelectedTypos = {};
    if (selectedSlideTypos) {
      Object.entries(selectedSlideTypos).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          filteredSelectedTypos[key] = value;
        }
      });
    }

    // edited_suggestions 필터링: 원래 suggestion과 다른 것만
    const filteredEditedSuggestions = {};
    if (editedSuggestions) {
      Object.entries(editedSuggestions).forEach(([key, editedValue]) => {
        // key 형식: "slide_0_1" → slideIndex=0, typoIndex=1
        const parts = key.split('_');
        if (parts.length >= 3 && parts[0] === 'slide') {
          const slideIndex = parseInt(parts[1], 10);
          const typoIndex = parseInt(parts[2], 10);
          const typo = slideTypoResults[slideIndex]?.[typoIndex];
          // 원래 suggestion과 다른 경우만 포함
          if (typo && editedValue !== (typo.suggestion || '')) {
            filteredEditedSuggestions[key] = editedValue;
          }
        }
      });
    }

    const hasSelectedTypos = Object.keys(filteredSelectedTypos).length > 0;
    const hasActualEdits = Object.keys(filteredEditedSuggestions).length > 0;

    // 선택된 것도 없고 실제 수정사항도 없으면 기존 draft 삭제
    if (!hasSelectedTypos && !hasActualEdits) {
      await deleteTypoDraft(selectedPpt);
      showAlertModal(t('draft.deleteSuccess'), 'success');
      return;
    }

    // 필터링된 데이터로 저장
    const result = await saveTypoDraft(selectedPpt, userId, filteredSelectedTypos, filteredEditedSuggestions);
    if (result) {
      showAlertModal(t('draft.saveSuccess'), 'success');
    } else {
      showAlertModal(t('draft.saveError'), 'error');
    }
  }, [selectedPpt, userId, selectedSlideTypos, editedSuggestions, slideTypoResults, showAlertModal, t]);

  // 색상 분석 훅
  const {
    allSlidesColorResults,
    colorCheckStatus,
    isAnalyzing,
    checkProgress,
    slideSize,
    totalViolations: colorTotalViolations,
    hasColorDraft,
    loadedColorDraft,
    analyzeAllSlidesColors,
    handleResultThumbnailClick,
    getCurrentSlideResult,
    loadSlideResultIfNeeded: loadColorSlideResultIfNeeded,
    handlePPTModification,
    applyColorDraft,
    clearColorDraft
  } = useColorAnalysis(slides, selectedPpt, userId, colors, showAlertModal, showConfirmModal, setIsModifyingColor, handleColorAnalysisComplete, t);

  // 폰트 분석 훅
  const {
    allSlidesFontResults,
    fontCheckStatus,
    isAnalyzing: isFontAnalyzing,
    checkProgress: fontCheckProgress,
    slideSize: fontSlideSize,
    totalViolations: fontTotalViolations,
    hasFontDraft,
    loadedFontDraft,
    analyzeAllSlidesFonts,
    // handleResultThumbnailClick는 openFontResultModal로 대체됨
    getCurrentSlideResult: getCurrentFontSlideResult,
    loadSlideResultIfNeeded: loadFontSlideResultIfNeeded,
    handlePPTModification: handleFontPPTModification,
    applyFontDraft,
    clearFontDraft
  } = useFontAnalysis(slides, selectedPpt, userId, fonts, showAlertModal, showConfirmModal, setIsModifyingFont, handleFontAnalysisComplete, t);

  // 번역 분석 훅
  const {
    isTranslating,
    translateProgress,
    translatedLanguages,
    translatePPT,
    downloadTranslatedPPT
  } = useTranslateAnalysis(selectedPpt, userId, credit, predictionData, showAlertModal, showConfirmModal, t);

  // Supabase 세션 확인 및 사용자 정보 로드
  useEffect(() => {
    console.log('🔍 Supabase 세션 확인 시작');

    const checkAuth = async () => {
      try {
        const res = await fetch(`${CLOUD_FUNCTION_URL}/auth/me`, {
          credentials: 'include',
        });
        const data = await res.json();

        if (data.authenticated && data.user) {
          const supabaseUserId = data.user.user_id;
          console.log('✅ 사용자 로그인됨:', supabaseUserId);

          // Firebase Custom Token으로 Firestore 인증을 먼저 끝낸다.
          // setUserId()를 하면 loadPptList/브랜드키트 effect가 즉시 Firestore를
          // 조회하는데, 그 전에 Firebase 인증이 완료돼 있어야 request.auth가 채워져
          // 보안규칙 통과(PERMISSION_DENIED 방지)된다.
          if (data.firebase_token) {
            try {
              await signInWithCustomToken(auth, data.firebase_token);
              console.log('✅ Firebase 인증 완료 (Firestore 접근 가능)');
            } catch (fbError) {
              console.error('⚠️ Firebase 인증 실패:', fbError);
            }
          }

          // Firebase 인증 후 userId 설정 → 데이터 로딩 effect가 인증된 상태에서 실행
          setUserId(supabaseUserId);
          setUserEmail(data.user.email || '');

          // Firestore에서 사용자 정보 가져오기
          try {
            const userDocRef = doc(db, 'USERS', supabaseUserId);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
              const userData = userDoc.data();
              setDisplayName(userData.displayName || data.user.email?.split('@')[0] || '사용자');
              const settings = userData.settings || {};
              setMaxUploadSizeMb(settings.maxUploadSizeMb ?? 100);
              setMaxPptCount(settings.maxPptCount ?? 10);
              setLanguage(settings.language ?? 'ko');
              console.log(`✅ 사용자 정보 로드: ${userData.displayName}`);
            } else {
              console.log('⚠️ Firestore 사용자 문서 없음. 기본값 사용');
              setDisplayName(data.user.email?.split('@')[0] || '사용자');
            }
          } catch (error) {
            console.error('❌ 사용자 정보 로드 실패:', error);
            setDisplayName(data.user.email?.split('@')[0] || '사용자');
          }

          // Supabase에서 크레딧 잔액 조회
          try {
            const creditRes = await fetch(
              `${CLOUD_FUNCTION_URL}/api/credit/balance?user_id=${supabaseUserId}`,
              { credentials: 'include' }
            );
            const creditData = await creditRes.json();
            if (creditData.success) {
              setCredit(creditData.credit || 0);
              console.log('💰 크레딧 로드:', creditData.credit);
            }
          } catch (error) {
            console.error('❌ 크레딧 조회 실패:', error);
          }
        } else {
          // 미인증 → 로그인 페이지로
          console.log('❌ 사용자 미인증');
          setUserId('');
          setUserEmail('');
          setDisplayName('');
          setCredit(0);
          navigate('/login');
        }
      } catch (error) {
        console.error('❌ 세션 확인 오류:', error);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate, CLOUD_FUNCTION_URL]);

  // Error 감지하여 AlertModal 표시
  useEffect(() => {
    if (error) {
      showAlertModal(error, 'error');
    }
  }, [error, showAlertModal]);

  // PPT 데이터 로드
  const loadPptData = useCallback(async (docId, isNewUpload = false) => {
    try {
      // 슬라이드 데이터 가져오기
      const slideData = await fetchPptSlides(docId);
      
      if (slideData.slides) {
        setSlides(slideData.slides);
        setCurrentSlideIndex(0);

        // 슬라이드 크기 설정 (오타 위치 매칭용)
        setTypoSlideSize(slideData.slideSize || null);

        // 크레딧 예측 데이터 설정
        setPredictionData(slideData.predictionData || null);

        // 모든 슬라이드의 오타검사 결과를 한번에 로드
        const typoResults = {};
        const checkStatus = {};
        
        try {
          const allTypoData = await fetchAllSlideTypoResults(docId);
          
          if (allTypoData.slides) {
            
            // 각 슬라이드의 오타 결과 처리
            for (let i = 0; i < slideData.slides.length; i++) {
              const slideTypoData = allTypoData.slides.find(s => s.slide_index === i);
              
              // 새로 업로드된 파일인 경우 모든 슬라이드를 미검사 상태로 설정
              if (isNewUpload) {
                checkStatus[i] = {
                  typoCount: 0,
                  checking: false,
                  hasBeenChecked: false
                };
              } else if (slideTypoData && slideTypoData.has_typo_check) {
                // 오타검사를 한 적이 있는 경우
                const typos = slideTypoData.typos || [];
                typoResults[i] = typos;

                checkStatus[i] = {
                  typoCount: typos.length,
                  checking: false,
                  hasBeenChecked: true
                };
                
              } else {
                // 오타검사를 한 적이 없는 경우
                checkStatus[i] = {
                  typoCount: 0,
                  checking: false,
                  hasBeenChecked: false
                };
                
              }
            }
          } else {
            // 배치 API 호출 실패 시 모든 슬라이드를 미검사 상태로 설정
            for (let i = 0; i < slideData.slides.length; i++) {
              checkStatus[i] = {
                typoCount: 0,
                checking: false,
                hasBeenChecked: false
              };
            }
          }
        } catch (batchError) {
          console.error('❌ 배치 오타검사 결과 로드 실패:', batchError);
          // 에러 발생 시 모든 슬라이드를 미검사 상태로 설정
          for (let i = 0; i < slideData.slides.length; i++) {
            checkStatus[i] = {
              typoCount: 0,
              checking: false,
              hasBeenChecked: false
            };
          }
        }
        
        setSlideTypoResults(typoResults);
        setSlideTypoCheckStatus(checkStatus);

        // 전체 텍스트 가져오기
        const totalData = await fetchTotalText(docId);
        setTotalText(totalData.total_text || '');
        setPdfUrl(totalData.pdf_url || null);  // PDF URL만 저장 (실제 다운로드는 모달에서)
        setPdfDoc(null);  // 새 PPT 선택 시 캐시된 PDF 초기화
      }
    } catch (e) {
      console.error('❌ PPT 데이터 로드 실패:', e);
      setSlides([]);
      setTotalText('');
      setPredictionData(null);
      resetTypoStates();
    }
  }, [setSlideTypoResults, setSlideTypoCheckStatus, resetTypoStates]);

  // PPT 리스트 불러오기 함수
  const loadPptList = useCallback(async (skipAutoLoad = false) => {
    if (!userId) return;

    try {
      const data = await fetchPptList(userId);
      setPptItems(data.pptItems || []);

      // 초기 로드 시에만 자동으로 첫 번째 PPT 로드 (업로드 후에는 실행 안함)
      if (!skipAutoLoad && data.pptItems && data.pptItems.length > 0 && !selectedPpt && slides.length === 0) {
        const firstItem = data.pptItems[0];
        if (firstItem?.docId) {
          setSelectedPpt(firstItem.docId);
          loadPptData(firstItem.docId);
        }
      }
    } catch (e) {
      console.error('PPT 리스트 로드 실패:', e);
      setPptItems([]);
    }
  }, [userId, loadPptData, selectedPpt, slides.length]); // slides.length 추가

  useEffect(() => {
    if (userId) {
      loadPptList();
    }
  }, [userId, loadPptList]);

  // Credit 주기적 업데이트 (Supabase 기반, 30초 간격 폴링)
  useEffect(() => {
    if (!userId) return;

    console.log('💰 Credit 폴링 시작:', userId);

    const fetchCredit = async () => {
      try {
        const res = await fetch(
          `${CLOUD_FUNCTION_URL}/api/credit/balance?user_id=${userId}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data.success) {
          setCredit(data.credit || 0);
        }
      } catch (error) {
        console.error('❌ Credit 폴링 오류:', error);
      }
    };

    // 초기 로드는 auth에서 이미 했으므로 30초 후부터 폴링
    const interval = setInterval(fetchCredit, 30000);

    return () => {
      console.log('💰 Credit 폴링 해제');
      clearInterval(interval);
    };
  }, [userId, CLOUD_FUNCTION_URL]);

  // 세션 변경 감지 리스너 (다른 기기에서 로그인 시 강제 로그아웃)
  // 다중 로그인 허용을 위해 비활성화
  // useEffect(() => {
  //   if (!userId) return;

  //   console.log('🔐 세션 변경 리스너 시작:', userId);

  //   const unsubscribe = subscribeToSessionChanges(userId, async (deviceInfo) => {
  //     // 다른 기기에서 로그인 감지
  //     setForceLogoutDeviceInfo(deviceInfo);
  //     setShowForceLogoutModal(true);
  //   });

  //   return () => {
  //     console.log('🔐 세션 변경 리스너 해제');
  //     unsubscribe();
  //   };
  // }, [userId]);

  // 강제 로그아웃 처리 (다중 로그인 허용으로 비활성화)
  // const handleForceLogout = async () => {
  //   setShowForceLogoutModal(false);
  //   await auth.signOut();
  //   navigate('/login');
  // };

  // PPT 선택 핸들러 (select dropdown용)
  const handleSelectPpt = async (e) => {
    const newSelectedDocId = e.target.value;
    setSelectedPpt(newSelectedDocId);

    if (newSelectedDocId) {
      await loadPptData(newSelectedDocId, false);
    } else {
      setSlides([]);
      setTotalText('');
      resetTypoStates();
    }
  };

  // PPT 선택 핸들러 (프로젝트 화면에서 클릭용)
  const handleSelectPptFromProject = async (docId) => {
    setSelectedPpt(docId);
    if (docId) {
      await loadPptData(docId, false);
      // 분석도구 화면으로 전환 (현재 활성화된 탭 유지)
      // pptActiveTab은 그대로 유지 (칼라검사 중이었으면 칼라검사로, 폰트검사 중이었으면 폰트검사로)
      setActiveView('analysis');
    }
  };

  // PPT 업로드 핸들러
  const handlePptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    await handleUpload(file, userId, async (result) => {

      if (result && result.doc_id) {
        // 업로드 완료 시 pdf_url 저장 (small_thumbnail_url 생성 시점에 함께 전달됨)
        if (result.pdf_url) {
          setPdfUrl(result.pdf_url);
        }

        // 백엔드에서 슬라이드 개수만 반환하는 경우
        if (typeof result.slides === 'number') {
          try {
            await loadPptData(result.doc_id, true); // 새 업로드임을 표시
            resetTypoStates();
            // 새 업로드 후 PPT 리스트 업데이트 및 선택 설정
            setTimeout(async () => {
              try {
                const data = await fetchPptList(userId);
                // 기존 pptItems에서 이미 업데이트된 썸네일 유지
                setPptItems(prevItems => {
                  const thumbnailMap = {};
                  prevItems.forEach(item => {
                    if (item.smallThumbnailUrl) {
                      thumbnailMap[item.docId] = item.smallThumbnailUrl;
                    }
                  });
                  return (data.pptItems || []).map(item => ({
                    ...item,
                    smallThumbnailUrl: thumbnailMap[item.docId] || item.smallThumbnailUrl
                  }));
                });

                // 방금 업로드한 PPT를 선택 상태로 설정 (docId로 설정)
                const uploadedItem = data.pptItems?.find(item => item.docId === result.doc_id);
                if (uploadedItem) {
                  setSelectedPpt(result.doc_id);
                }
              } catch (error) {
                console.error('❌ PPT 리스트 업데이트 실패:', error);
              }
            }, 100);
          } catch (error) {
            console.error('❌ 슬라이드 데이터 로딩 실패:', error);
            setError('업로드는 완료되었지만 슬라이드 데이터를 불러오는데 실패했습니다.');
          }
        }
        // 백엔드에서 슬라이드 배열을 직접 반환하는 경우 (기존 로직)
        else if (result.slides && Array.isArray(result.slides)) {
          setSlides(result.slides);
          setCurrentSlideIndex(0);
          resetTypoStates();
          initializeSlideTypoStatus(result.slides.length);
          loadPptList();
        }
        else {
          console.error('❌ 알 수 없는 슬라이드 데이터 형태:', result);
          setError('업로드된 파일의 슬라이드 정보를 처리할 수 없습니다.');
        }
      } else {
        console.error('❌ 업로드 결과에 doc_id가 없음:', result);
        setError('업로드 처리 중 문제가 발생했습니다.');
      }
    });

    // 파일 선택 초기화
    e.target.value = '';
  };

  // PPT 삭제 핸들러 (docId로 삭제)
  const handleDeletePpt = async (docId) => {
    const pptItem = pptItems.find(item => item.docId === docId);
    if (!pptItem) return;

    const confirmed = await showConfirmModal(t('project.deleteConfirm', { filename: pptItem.filename }));
    if (!confirmed) return;

    try {
      const result = await deletePptDocument(docId, userId);

      if (result.success) {
        showAlertModal(t('project.deleteSuccess'), 'success');
        loadPptList();
        setSlides([]);
        setTotalText('');
        setSelectedPpt('');
        resetTypoStates();
      } else {
        showAlertModal(t('project.deleteError', { error: result.error || t('project.deleteErrorUnknown') }), 'error');
      }
    } catch (e) {
      showAlertModal(t('project.deleteError', { error: e.message }), 'error');
    }
  };


  // 썸네일 Lazy Loading 함수
  const loadThumbnailsForSlide = useCallback(async (targetIndex) => {
    if (!selectedPpt || slides.length === 0) return;

    // 현재 슬라이드 기준 앞뒤 2개씩 (총 5개) 범위 계산
    const startIndex = Math.max(0, targetIndex - 2);
    const endIndex = Math.min(slides.length - 1, targetIndex + 2);

    // 이미 로드된 썸네일은 제외하고, 로드가 필요한 인덱스만 찾기
    const indicesToLoad = [];
    for (let i = startIndex; i <= endIndex; i++) {
      if (!slides[i].thumbnail_url) {
        indicesToLoad.push(i);
      }
    }

    // 로드할 썸네일이 없으면 종료
    if (indicesToLoad.length === 0) return;

    // 범위 내 썸네일 로드
    const loadStart = Math.min(...indicesToLoad);
    const loadEnd = Math.max(...indicesToLoad);
    const thumbnails = await fetchSlideThumbnails(selectedPpt, loadStart, loadEnd);

    // slides 상태 업데이트
    if (Object.keys(thumbnails).length > 0) {
      setSlides(prevSlides => {
        const newSlides = [...prevSlides];
        Object.entries(thumbnails).forEach(([index, url]) => {
          const idx = parseInt(index, 10);
          if (newSlides[idx] && !newSlides[idx].thumbnail_url) {
            newSlides[idx] = { ...newSlides[idx], thumbnail_url: url };
          }
        });
        return newSlides;
      });
    }
  }, [selectedPpt, slides]);

  // 슬라이드 네비게이션
  const goToPrevSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      const newIndex = currentSlideIndex - 1;
      setIsSlideLoading(true);
      setCurrentSlideIndex(newIndex);
      loadThumbnailsForSlide(newIndex);
      setTimeout(() => setIsSlideLoading(false), 300);
    }
  }, [currentSlideIndex, loadThumbnailsForSlide]);

  const goToNextSlide = useCallback(() => {
    if (currentSlideIndex < slides.length - 1) {
      const newIndex = currentSlideIndex + 1;
      setIsSlideLoading(true);
      setCurrentSlideIndex(newIndex);
      loadThumbnailsForSlide(newIndex);
      setTimeout(() => setIsSlideLoading(false), 300);
    }
  }, [currentSlideIndex, slides.length, loadThumbnailsForSlide]);

  const goToSlide = useCallback((slideIndex) => {
    if (slideIndex !== currentSlideIndex) {
      setIsSlideLoading(true);
      setCurrentSlideIndex(slideIndex);
      loadThumbnailsForSlide(slideIndex);
      setTimeout(() => setIsSlideLoading(false), 300);
    }
  }, [currentSlideIndex, loadThumbnailsForSlide]);

  // 현재 슬라이드의 썸네일이 없으면 자동으로 로드
  useEffect(() => {
    if (slides.length > 0 && currentSlideIndex >= 0 && !slides[currentSlideIndex]?.thumbnail_url) {
      loadThumbnailsForSlide(currentSlideIndex);
    }
  }, [slides, currentSlideIndex, loadThumbnailsForSlide]);

  // 새로고침 시 session_id 쿠키 삭제
  useEffect(() => {
    document.cookie = 'session_id=; Max-Age=0; path=/;';
  }, []);

  // 언어 설정 변경 시 i18n 언어 변경
  useEffect(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
      console.log('🌐 언어 변경:', language);
    }
  }, [language, i18n]);


  const wrappedApplySlideTypoCorrections = async () => {
    await applySlideTypoCorrections();
  };

  // 브랜드키트 저장 핸들러
  const handleBrandKitSave = () => {
    // 강제 업데이트 트리거
    setBrandKitUpdateTrigger(prev => prev + 1);
    // localStorage 이벤트도 함께 트리거 (fallback)
    window.dispatchEvent(new Event('storage'));
  };

  // 화면 전환 핸들러 (브랜드키트 변경사항 확인 포함)
  const handleActiveViewChange = async (newView) => {
    // 브랜드키트에서 다른 화면으로 이동할 때 변경사항 확인
    if (activeView === 'brandkit' && newView !== 'brandkit') {
      if (window.brandKitCheckUnsavedChanges) {
        const canLeave = await window.brandKitCheckUnsavedChanges();
        if (!canLeave) {
          return; // 이동 취소
        }
      }
    }
    setActiveView(newView);
  };

  // draft 변경 개수 계산 헬퍼 함수
  const countDraftChanges = (draft) => {
    if (!draft || typeof draft !== 'object') return 0;
    return Object.values(draft).reduce((total, slideChanges) => {
      if (typeof slideChanges === 'object') {
        return total + Object.keys(slideChanges).length;
      }
      return total;
    }, 0);
  };

  // 두 객체가 동일한지 비교하는 헬퍼 함수
  const isObjectEqual = (obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };

  // ColorResultModal 관련 함수들
  const openColorResultModal = async (slideIndex) => {
    setModalSlideIndex(slideIndex);
    loadThumbnailsForSlide(slideIndex);
    loadColorSlideResultIfNeeded(slideIndex);
    setShowColorResultModal(true);

    // Draft가 있으면 확인 모달 표시
    if (hasColorDraft && loadedColorDraft) {
      const draftCount = countDraftChanges(loadedColorDraft);
      const confirmed = await showConfirmModal(t('draft.colorDraftLoaded', { count: draftCount }));
      if (confirmed) {
        // 확인: draft 적용
        const draft = applyColorDraft();
        if (draft) {
          setViolationColorChanges(draft);
          // 초기 상태를 draft로 설정
          colorInitialStateRef.current = JSON.parse(JSON.stringify(draft));
        }
      } else {
        // 취소: draft 무시, 빈 상태로 시작
        setViolationColorChanges({});
        // 초기 상태를 빈 객체로 설정
        colorInitialStateRef.current = {};
      }
      // 확인/취소 상관없이 draft 상태 초기화 (다시 물어보지 않음)
      clearColorDraft();
    } else {
      // draft가 없으면 현재 상태를 초기 상태로 저장
      colorInitialStateRef.current = JSON.parse(JSON.stringify(violationColorChanges));
    }
  };

  const closeColorResultModal = async () => {
    // 초기 상태와 현재 상태 비교
    const hasChanges = !isObjectEqual(colorInitialStateRef.current, violationColorChanges);

    if (hasChanges) {
      const shouldSave = await showConfirmModal(t('draft.unsavedChanges'));
      if (shouldSave) {
        // 임시저장 실행
        await handleSaveColorDraft(violationColorChanges);
        // 저장 후 초기 상태 업데이트
        colorInitialStateRef.current = JSON.parse(JSON.stringify(violationColorChanges));
      }
    }

    setShowColorResultModal(false);
    setModalSlideIndex(0);
    // 변경사항 유지 (모달 닫아도 초기화하지 않음)
  };

  const goToPrevModalSlide = () => {
    if (modalSlideIndex > 0) {
      const newIndex = modalSlideIndex - 1;
      setModalSlideIndex(newIndex);
      loadThumbnailsForSlide(newIndex);
      loadColorSlideResultIfNeeded(newIndex);
      // 변경사항 유지 (초기화하지 않음)
    }
  };

  const goToNextModalSlide = () => {
    if (modalSlideIndex < slides.length - 1) {
      const newIndex = modalSlideIndex + 1;
      setModalSlideIndex(newIndex);
      loadThumbnailsForSlide(newIndex);
      loadColorSlideResultIfNeeded(newIndex);
      // 변경사항 유지 (초기화하지 않음)
    }
  };

  const handleViolationColorChange = (violationIndex, newColor) => {
    setViolationColorChanges(prev => ({
      ...prev,
      [modalSlideIndex]: {
        ...(prev[modalSlideIndex] || {}),
        [violationIndex]: newColor
      }
    }));
  };

  const handleResetViolationColor = (violationIndex) => {
    setViolationColorChanges(prev => {
      const newChanges = { ...prev };
      if (newChanges[modalSlideIndex]) {
        delete newChanges[modalSlideIndex][violationIndex];
        // 빈 객체면 슬라이드 키도 삭제
        if (Object.keys(newChanges[modalSlideIndex]).length === 0) {
          delete newChanges[modalSlideIndex];
        }
      }
      return newChanges;
    });
  };

  // FontResultModal 관련 함수들 (ColorResultModal과 동일한 구조)
  const openFontResultModal = async (slideIndex) => {
    console.log('[FONT MODAL DEBUG] openFontResultModal called with slideIndex:', slideIndex);
    setModalSlideIndex(slideIndex);
    loadThumbnailsForSlide(slideIndex);
    loadFontSlideResultIfNeeded(slideIndex);
    setShowFontResultModal(true);

    // Draft가 있으면 확인 모달 표시
    if (hasFontDraft && loadedFontDraft) {
      const draftCount = countDraftChanges(loadedFontDraft);
      const confirmed = await showConfirmModal(t('draft.fontDraftLoaded', { count: draftCount }));
      if (confirmed) {
        // 확인: draft 적용
        const draft = applyFontDraft();
        if (draft) {
          setViolationFontChanges(draft);
          // 초기 상태를 draft로 설정
          fontInitialStateRef.current = JSON.parse(JSON.stringify(draft));
        }
      } else {
        // 취소: draft 무시, 빈 상태로 시작
        setViolationFontChanges({});
        // 초기 상태를 빈 객체로 설정
        fontInitialStateRef.current = {};
      }
      // 확인/취소 상관없이 draft 상태 초기화 (다시 물어보지 않음)
      clearFontDraft();
    } else {
      // draft가 없으면 현재 상태를 초기 상태로 저장
      fontInitialStateRef.current = JSON.parse(JSON.stringify(violationFontChanges));
    }
  };

  const closeFontResultModal = async () => {
    // 초기 상태와 현재 상태 비교
    const hasChanges = !isObjectEqual(fontInitialStateRef.current, violationFontChanges);

    if (hasChanges) {
      const shouldSave = await showConfirmModal(t('draft.unsavedChanges'));
      if (shouldSave) {
        // 임시저장 실행
        await handleSaveFontDraft(violationFontChanges);
        // 저장 후 초기 상태 업데이트
        fontInitialStateRef.current = JSON.parse(JSON.stringify(violationFontChanges));
      }
    }

    setShowFontResultModal(false);
    setModalSlideIndex(0);
    // 변경사항 유지 (모달 닫아도 초기화하지 않음)
  };

  const goToPrevFontModalSlide = () => {
    if (modalSlideIndex > 0) {
      const newIndex = modalSlideIndex - 1;
      setModalSlideIndex(newIndex);
      loadThumbnailsForSlide(newIndex);
      loadFontSlideResultIfNeeded(newIndex);
      // 변경사항 유지 (초기화하지 않음)
    }
  };

  const goToNextFontModalSlide = () => {
    if (modalSlideIndex < slides.length - 1) {
      const newIndex = modalSlideIndex + 1;
      setModalSlideIndex(newIndex);
      loadThumbnailsForSlide(newIndex);
      loadFontSlideResultIfNeeded(newIndex);
      // 변경사항 유지 (초기화하지 않음)
    }
  };

  const handleViolationFontChange = (violationIndex, newFont) => {
    setViolationFontChanges(prev => ({
      ...prev,
      [modalSlideIndex]: {
        ...(prev[modalSlideIndex] || {}),
        [violationIndex]: newFont
      }
    }));
  };

  const handleResetViolationFont = (violationIndex) => {
    setViolationFontChanges(prev => {
      const newChanges = { ...prev };
      if (newChanges[modalSlideIndex]) {
        delete newChanges[modalSlideIndex][violationIndex];
        // 빈 객체면 슬라이드 키도 삭제
        if (Object.keys(newChanges[modalSlideIndex]).length === 0) {
          delete newChanges[modalSlideIndex];
        }
      }
      return newChanges;
    });
  };

  // getCurrentFontSlideResult와 handleFontPPTModification은 이제 useFontAnalysis 훅에서 가져옴

  // 모바일 감지
  const isMobile = window.innerWidth <= 768 || window.innerHeight > window.innerWidth;
  const styles = createAppStyles(sidebarWidth, sidebarOpen, isMobile);

  return (
    <div style={styles.container}>
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        sidebarWidth={sidebarWidth}
        isMobile={isMobile}
        uploading={uploading}
        uploadProgress={uploadProgress}
        uploadDone={uploadDone}
        uploadStatus={uploadStatus}
        error={error}
        selectedPpt={selectedPpt}
        handleSelectPpt={handleSelectPpt}
        handleDeletePpt={handleDeletePpt}
        totalText={totalText}
        slides={slides}
        handlePptUpload={handlePptUpload}
        showAlertModal={showAlertModal}
        displayName={displayName}
        userCredit={credit}
        pptActiveTab={pptActiveTab}
        setPptActiveTab={setPptActiveTab}
        CLOUD_FUNCTION_URL={CLOUD_FUNCTION_URL}
        showConfirmModal={showConfirmModal}
        navigate={navigate}
        activeView={activeView}
        setActiveView={handleActiveViewChange}
      />

      {/* Main Content Area */}
      <div style={styles.mainContent}>
        {/* 프로젝트 화면 */}
        {activeView === 'project' && (
          <>
            {/* 프로젝트 헤더 */}
            <div style={styles.header}>
              <div>
                <h1 style={styles.pageTitle}>{t('header.fileManagement')}</h1>
                <p style={styles.pageSubtitle}>{t('header.fileManagementDesc')}</p>
              </div>
            </div>
            {/* 프로젝트 뷰 */}
            <ProjectView
              pptItems={pptItems}
              selectedPpt={selectedPpt}
              onSelectPpt={handleSelectPptFromProject}
              onDeletePpt={handleDeletePpt}
              onUpload={handlePptUpload}
              uploading={uploading}
              uploadProgress={uploadProgress}
              uploadDone={uploadDone}
              uploadStatus={uploadStatus}
              isMobile={isMobile}
              showAlertModal={showAlertModal}
              maxUploadSizeMb={maxUploadSizeMb}
              maxPptCount={maxPptCount}
            />
          </>
        )}

        {/* 분석도구 화면 */}
        {activeView === 'analysis' && (
          <>
            {/* Header - 새로운 라이트 테마 */}
            <div style={styles.header}>
              <div>
                <h1 style={styles.pageTitle}>
                  {selectedPpt
                    ? t('header.analysisResult', { filename: pptItems.find(item => item.docId === selectedPpt)?.filename || 'PPT' })
                    : t('header.analysisResultDefault')}
                </h1>
                <p style={{...styles.pageSubtitle, fontSize: isMobile ? '12px' : styles.pageSubtitle.fontSize}}>
                  {t('header.analysisResultDesc')}
                </p>
              </div>
              <div style={styles.tabContainer}>
                <button
                  style={styles.pptTabButton(pptActiveTab === 'typo')}
                  onClick={() => setPptActiveTab('typo')}
                >
                  {t('tabs.typo')}
                </button>
                <button
                  style={styles.pptTabButton(pptActiveTab === 'color')}
                  onClick={() => setPptActiveTab('color')}
                >
                  {t('tabs.color')}
                </button>
                <button
                  style={styles.pptTabButton(pptActiveTab === 'font')}
                  onClick={() => setPptActiveTab('font')}
                >
                  {t('tabs.font')}
                </button>
                <button
                  style={styles.pptTabButton(pptActiveTab === 'translate')}
                  onClick={() => setPptActiveTab('translate')}
                >
                  {t('tabs.translate')}
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <ErrorBoundary>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <PPTCheckTabs
                    activeTab={pptActiveTab}
                    setActiveTab={setPptActiveTab}
                    styles={styles}
                    slides={slides}
                    currentSlideIndex={currentSlideIndex}
                    setCurrentSlide={setCurrentSlideIndex}
                    isSlideLoading={isSlideLoading}
                    goToPrevSlide={goToPrevSlide}
                    goToNextSlide={goToNextSlide}
                    goToSlide={goToSlide}
                    isMobile={isMobile}
                    checkSlideTypos={checkSlideTypos}
                    checkAllSlidesTypos={checkAllSlidesTypos}
                    checkAllProgress={checkAllProgress}
                    isTypoAnalyzingAll={isTypoAnalyzingAll}
                    isTypoAnySingleSlideChecking={isTypoAnySingleSlideChecking}
                    slideTypoResults={slideTypoResults}
                    slideTypoCheckStatus={slideTypoCheckStatus}
                    selectedSlideTypos={selectedSlideTypos}
                    toggleSlideTypoSelection={toggleSlideTypoSelection}
                    selectAllSlideTypos={selectAllSlideTypos}
                    deselectAllSlideTypos={deselectAllSlideTypos}
                    editedSuggestions={editedSuggestions}
                    updateEditedSuggestion={updateEditedSuggestion}
                    getEditedSuggestion={getEditedSuggestion}
                    applyingTypoCorrections={applyingTypoCorrections}
                    applySlideTypoCorrections={wrappedApplySlideTypoCorrections}
                    onModalOpen={openColorResultModal}
                    colors={colors}
                    allSlidesColorResults={allSlidesColorResults}
                    colorCheckStatus={colorCheckStatus}
                    isAnalyzing={isAnalyzing}
                    checkProgress={checkProgress}
                    analyzeAllSlidesColors={analyzeAllSlidesColors}
                    handleResultThumbnailClick={handleResultThumbnailClick}
                    fonts={fonts}
                    allSlidesFontResults={allSlidesFontResults}
                    fontCheckStatus={fontCheckStatus}
                    isFontAnalyzing={isFontAnalyzing}
                    fontCheckProgress={fontCheckProgress}
                    analyzeAllSlidesFonts={analyzeAllSlidesFonts}
                    handleFontResultThumbnailClick={openFontResultModal}
                    isTranslating={isTranslating}
                    translateProgress={translateProgress}
                    translatedLanguages={translatedLanguages}
                    translatePPT={translatePPT}
                    downloadTranslatedPPT={downloadTranslatedPPT}
                    pdfUrl={pdfUrl}
                    pdfDoc={pdfDoc}
                    setPdfDoc={setPdfDoc}
                    showAlertModal={showAlertModal}
                    showConfirmModal={showConfirmModal}
                    onFontChange={handleFontChange}
                    showTypoResultModal={showTypoResultModal}
                    setShowTypoResultModal={setShowTypoResultModal}
                    selectedPpt={selectedPpt}
                    loadThumbnailsForSlide={loadThumbnailsForSlide}
                    typoSlideSize={typoSlideSize}
                    onSaveTypoDraft={handleSaveTypoDraft}
                    hasTypoDraft={hasTypoDraft}
                    loadedTypoDraft={loadedTypoDraft}
                    applyTypoDraft={applyTypoDraft}
                    clearTypoDraft={clearTypoDraft}
                  />
                </div>
              </ErrorBoundary>
            </div>
          </>
        )}

        {/* 브랜드키트 화면 */}
        {activeView === 'brandkit' && (
          <BrandKitViewer
            userId={userId}
            showAlertModal={showAlertModal}
            showConfirmModal={showConfirmModal}
            onSave={handleBrandKitSave}
            isMobile={isMobile}
          />
        )}

        {/* 설정 화면 */}
        {activeView === 'settings' && (
          <SettingsView
            userId={userId}
            userEmail={userEmail}
            showAlertModal={showAlertModal}
            isMobile={isMobile}
            maxUploadSizeMb={maxUploadSizeMb}
            maxPptCount={maxPptCount}
            language={language}
            onSettingsChange={(newSettings) => {
              if (newSettings.language !== undefined) setLanguage(newSettings.language);
            }}
          />
        )}

        {/* 크레딧 내역 화면 */}
        {activeView === 'creditHistory' && (
          <CreditHistoryPage
            userId={userId}
            isMobile={isMobile}
          />
        )}
      </div>

      {/* Alert 모달 (통합) */}
      <AlertModal
        isOpen={showAlert}
        message={alertMessage}
        alertType={alertType}
        onClose={() => {
          closeAlert();
          setError(''); // error state 초기화
        }}
      />

      {/* Confirm 모달 (통합) */}
      <AlertModal
        type="confirm"
        isOpen={showConfirm}
        message={confirmMessage}
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />

      {/* ColorResultModal */}
      {showColorResultModal && (
        <ResultModal
          modalType="color"
          isOpen={showColorResultModal}
          onClose={closeColorResultModal}
          slides={slides}
          modalSlideIndex={modalSlideIndex}
          goToPrevModalSlide={goToPrevModalSlide}
          goToNextModalSlide={goToNextModalSlide}
          getCurrentSlideResult={getCurrentSlideResult}
          violationChanges={violationColorChanges[modalSlideIndex] || {}}
          allViolationChanges={violationColorChanges}
          handleViolationChange={handleViolationColorChange}
          handleResetViolation={handleResetViolationColor}
          handlePPTModification={handlePPTModification}
          slideSize={slideSize}
          isModifying={isModifyingColor}
          isMobile={isMobile}
          brandKitColors={colors}
          totalViolations={colorTotalViolations}
          onSaveDraft={handleSaveColorDraft}
          initialState={colorInitialStateRef.current}
        />
      )}

      {showFontResultModal && (
        <ResultModal
          modalType="font"
          isOpen={showFontResultModal}
          onClose={closeFontResultModal}
          slides={slides}
          modalSlideIndex={modalSlideIndex}
          goToPrevModalSlide={goToPrevFontModalSlide}
          goToNextModalSlide={goToNextFontModalSlide}
          getCurrentSlideResult={getCurrentFontSlideResult}
          violationChanges={violationFontChanges[modalSlideIndex] || {}}
          allViolationChanges={violationFontChanges}
          handleViolationChange={handleViolationFontChange}
          isModifying={isModifyingFont}
          handleResetViolation={handleResetViolationFont}
          handlePPTModification={handleFontPPTModification}
          slideSize={fontSlideSize}
          isMobile={isMobile}
          brandKitFonts={fonts}
          totalViolations={fontTotalViolations}
          onSaveDraft={handleSaveFontDraft}
          initialState={fontInitialStateRef.current}
        />
      )}

      {/* 강제 로그아웃 모달 (다중 로그인 허용으로 비활성화) */}
      {/* {showForceLogoutModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '28px',
            maxWidth: '420px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#fee2e2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '28px'
            }}>
              ⚠️
            </div>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '20px', color: '#1f2937' }}>
              {t('forceLogout.title')}
            </h2>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
              {t('forceLogout.message', { device: forceLogoutDeviceInfo || t('forceLogout.defaultDevice') })}
            </p>
            <button
              onClick={handleForceLogout}
              style={{
                width: '100%',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #1A62EA 0%, #3b82f6 100%)',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(26, 98, 234, 0.3)'
              }}
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      )} */}
    </div>
  );
}

// 메인 App 컴포넌트 - 라우팅 담당
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RenderPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/callback" element={<OAuthCallback />} />
        <Route path="/main" element={<PPTAnalysisPage />} />
        <Route path="/api-document" element={<ApiDocumentPage />} />
        <Route path="/editor" element={<EditorPage />} />
        <Route path="/editor/:projectId" element={<EditorPage />} />
      </Routes>
    </BrowserRouter>
  );
}